import { Mutation, Action } from 'vuex-module-decorators';
import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import { Resolve } from 'vue-di';
import { EdgeDocumentsApi } from '@trialcard/apigateway.client/edgeDocumentsApi';
import { PDFDocument, rgb, StandardFonts, PageSizes, PDFImage, PDFPage } from 'pdf-lib';
import { AxiosInstance, AxiosRequestConfig } from 'axios';
import EnrollmentModule from '~/store/enrollment';
import gender from '~/assets/gender';

export declare class PageStringObjects {
	type: string;
	label: string;
	value: string;
}
@InjectModule({ stateFactory: true }, module)
export default class DocumentModule extends InjectVuexModule {
	public static documentFontRegular = StandardFonts.Helvetica;
	public static documentFontBold = StandardFonts.HelveticaBold;
	public static fontSizeRegular = 12;
	public static fontSizeLarge = 20;

	@Resolve
	enrollment!: EnrollmentModule;

	@Resolve
	edgeDocumentsApi!: EdgeDocumentsApi;

	public hasDocument = false;

	@Action({ rawError: true })
	clear() {
		this.updateDocumentStatus(false);
	}

	@Action({ rawError: true })
	setDocumentStatus(newDocumentStatus: boolean) {
		this.hasDocument = newDocumentStatus;
	}

	@Action({ rawError: true })
	public async fetchDocument() {
		const document = await this.edgeDocumentsApi.fillablePdfGenerateFillablePdf({
			fileUrl: window.location.href.replace('thank-you', 'CO-PAY.TRIALCARD.web_co-pay_card.pdf'),
			outputFileName: 'copay-card',
			fileData: [{ name: 'memberNumber', value: this.enrollment.memberNumber || '' }],
		});
		return document;
	}

	@Mutation
	updateDocumentStatus(newDocumentStatus: boolean) {
		this.setDocumentStatus(newDocumentStatus);
	}

	public async createPage(strings: PageStringObjects[]) {
		const pdfDoc = await PDFDocument.create();
		const fontRegular = await pdfDoc.embedFont(DocumentModule.documentFontRegular);
		const fontBold = await pdfDoc.embedFont(DocumentModule.documentFontBold)

		// Add a blank page to the document
		const page = pdfDoc.addPage(PageSizes.Letter);
		const { width, height } = page.getSize();

		// Draw a adds the text to the coverpage
		strings.forEach((item, index) => {
			const isTitle = item.type === 'title';
			const size = isTitle ? DocumentModule.fontSizeLarge : DocumentModule.fontSizeRegular;

			// height measures from bottom-to-top (0 is the bottom, ~800 is the top)
			// (height - 50) -- determines the top margin
			const margin = (height - 50)

			// 1.5 * size -- determines the line height
			// index -- determines how many lines down that particular string is
			const titleLineSpacing = 1.5 * size * index;

			// multiplying by 1.8 -- nearly doubles line spacing to make room for value string
			const lineSpacing = (1.5 * size * index) * 2

			// add size and a little more to move the string into the empty space
			const extraLine = size + 2

			if (isTitle) {
				page.drawText(item.label, {
					x: 50,
					y: margin - titleLineSpacing,
					size: 20,
					font: fontBold,
					color: rgb(0, 0, 0),
				})
			} else {
				page.drawText(item.label, {
					x: 50,
					y: margin - (lineSpacing),
					size,
					font: fontBold,
					color: rgb(0, 0, 0),
				})
				page.drawText(item.value, {
					x: 50,
					y: margin - (lineSpacing + extraLine),
					size,
					font: fontRegular,
					color: rgb(0, 0, 0),
				})

			}

		}
		);

		return pdfDoc;
	}

	public async createPdf() {
		const strings = this.enrollment.prepPdfModel();

		const pdfDoc = await PDFDocument.create()

		for (const stringArray of strings) {
			const page = await this.createPage(stringArray)
			const [copy] = await pdfDoc.copyPages(page, [0])
			pdfDoc.addPage(copy)
		}

		const pdfBytes = await pdfDoc.save()

		const blob = new Blob([pdfBytes.buffer], { type: 'application/pdf' });

		return blob
	}

	public async uploadPdf() {
		const blob = await this.createPdf()
		const formData = new FormData();
		formData.append('type', '10001');
		formData.append('attachment', blob);

		const axios: AxiosInstance = (this.edgeDocumentsApi as any).axios;
		const axiosRequest: AxiosRequestConfig = {
			url: 'edge/documents/v1/Document/upload',
			method: 'POST',
			data: formData,
			headers: {
				'x-program-id': process.env.programId!,
			},
		};

		await Promise.all([
			(this.edgeDocumentsApi as any).authentications.ApiKey.applyToRequest(axiosRequest),
			(this.edgeDocumentsApi as any).authentications.Bearer.applyToRequest(axiosRequest),
			(this.edgeDocumentsApi as any).authentications.Token.applyToRequest(axiosRequest),
		]);

		const result = await axios.request(axiosRequest);
		return result;
	}

	public async downloadPdf() {
		const blob = await this.createPdf()
		const link = document.createElement('a');
		link.href = window.URL.createObjectURL(blob);
		const fileName = 'pdf';
		link.download = fileName;
		link.click();
	}

	public async combinePages(pages: PDFDocument[]) {
		const pdfDoc = await PDFDocument.create();

		const copiedPages = [] as PDFPage[]

		await pages.forEach(async (page, index) => {
			const [copyPage] = await pdfDoc.copyPages(page, [index])
			copiedPages.push(copyPage)
		})

		copiedPages.forEach(page => {
			pdfDoc.addPage(page)
		})

		const pdfBytes = await pdfDoc.save();

		// Serialize the PDFDocument to bytes (a Uint8Array)
		return pdfBytes;
	}
}
